import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Bar, Area, Cell } from 'recharts';
import CandlestickChart from './CandlestickChart';
import OscillatorChart from './OscillatorChart';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Switch } from './components/ui/switch';
import { Label } from './components/ui/label';
import { Alert, AlertDescription } from './components/ui/alert';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { TrendingUp, TrendingDown, Minus, BarChart3, Activity, Search, Menu, ChevronDown, X, Sun, Moon } from 'lucide-react';
import { useThemeStore } from './state/themeStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom Candlestick Shape for Recharts
const Candlestick = (props) => {
  const { x, y, width, height, low, high, openClose } = props;
  const isGrowing = openClose[1] > openClose[0];
  const color = isGrowing ? '#22c55e' : '#ef4444';
  const ratio = Math.abs(height / (openClose[0] - openClose[1]));
  
  return (
    <g stroke={color} fill="none" strokeWidth="2">
      <path
        d={`
          M ${x},${y}
          L ${x},${y + height}
          L ${x + width},${y + height}
          L ${x + width},${y}
          L ${x},${y}
        `}
        fill={color}
        opacity={0.8}
      />
    </g>
  );
};

function App() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [ticker, setTicker] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const darkMode = useThemeStore((s) => s.isDarkMode);
  const [activeTab, setActiveTab] = useState('analysis');
  const [dataSource, setDataSource] = useState('yahoo');
  const [marketRegion] = useState('india');
  const [indiaExchange, setIndiaExchange] = useState('NSE');
  const [chartType, setChartType] = useState('recharts');
  const [error, setError] = useState(null);
  const [chartDataWithIndicators, setChartDataWithIndicators] = useState([]);
  const [supportResistanceTimeframe, setSupportResistanceTimeframe] = useState('today');
  const [companyInfo, setCompanyInfo] = useState(null);
  const [chartTimeframe, setChartTimeframe] = useState('3m');
  const commonTickers = ['NIFTYBEES', 'RELIANCE', 'TCS', 'INFY', 'HDFCBANK', 'ICICIBANK', 'SBIN', 'BHARTIARTL', 'ITC', 'LT'];
  const [indiaSearchQuery, setIndiaSearchQuery] = useState('');
  const [indiaSearchResults, setIndiaSearchResults] = useState([]);
  const [indiaSearchOpen, setIndiaSearchOpen] = useState(false);
  const indiaSearchRef = useRef(null);
  const indiaSearchTimer = useRef(null);

  const searchIndiaStocks = useCallback(async (q) => {
    if (!q || q.length < 1) { setIndiaSearchResults([]); setIndiaSearchOpen(false); return; }
    try {
      const { data } = await axios.get(`${API}/india/securities`, {
        params: { search: q, page_size: 15 },
      });
      const results = data.items || data.data || [];
      setIndiaSearchResults(results);
      if (results.length > 0) setIndiaSearchOpen(true);
    } catch { setIndiaSearchResults([]); }
  }, []);

  useEffect(() => {
    clearTimeout(indiaSearchTimer.current);
    indiaSearchTimer.current = setTimeout(() => searchIndiaStocks(indiaSearchQuery), 150);
    return () => clearTimeout(indiaSearchTimer.current);
  }, [indiaSearchQuery, searchIndiaStocks]);

  useEffect(() => {
    const handler = (e) => {
      if (indiaSearchRef.current && !indiaSearchRef.current.contains(e.target))
        setIndiaSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const [mag7Data, setMag7Data] = useState({});
  const [mag7Timeframe, setMag7Timeframe] = useState('1month');
  const [mag7Loading, setMag7Loading] = useState(false);
  const [mag7GridLayout, setMag7GridLayout] = useState('4x2'); // 4x2, 3x3, 2x4, 1x8
  const [mag7Indicators, setMag7Indicators] = useState({
    sma5: true,
    sma50: true,
    sma100: true,
    sma200: true,
    bollinger: true,
    price: true,
    volume: true
  });

  // Options state
  const [optionsData, setOptionsData] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [riskLevel, setRiskLevel] = useState('Mid');
  const [userPrediction, setUserPrediction] = useState('Auto'); // Auto, Bullish, Bearish, Neutral
  const [debitCreditFilter, setDebitCreditFilter] = useState('Either'); // Either, Debit Only, Credit Only
  const llmSource = 'claude'; // Always use Claude API
  const [recentTickers, setRecentTickers] = useState([]); // Track last 8 analyzed tickers
  
  // Session ID for tracking user (stored in localStorage)
  const [sessionId] = useState(() => {
    let sid = localStorage.getItem('analyzerSessionId');
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('analyzerSessionId', sid);
    }
    return sid;
  });
  
  // News state
  const [newsData, setNewsData] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  
  // Load recent tickers from database on mount
  useEffect(() => {
    const fetchRecentTickers = async () => {
      try {
        const response = await axios.get(`${API}/recent-tickers/${sessionId}`);
        if (response.data.status === 'success') {
          const tickersList = response.data.tickers.map(t => t.ticker);
          setRecentTickers(tickersList);
        }
      } catch (error) {
        console.error('Failed to fetch recent tickers:', error);
      }
    };
    
    fetchRecentTickers();
  }, [sessionId]);
  
  // Multi-timeframe charts state
  const [showVolume, setShowVolume] = useState(true);
  const [multiChartData, setMultiChartData] = useState({
    '1h': [],
    '4h': [],
    '1d': [],
    '1w': []
  });
  
  // Earnings state
  const [earningsData, setEarningsData] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);

  // Helper function to add ticker to recent list
  const addToRecentTickers = async (tickerSymbol) => {
    const upperTicker = tickerSymbol.toUpperCase();
    if (!commonTickers.includes(upperTicker)) {
      try {
        // Save to database
        await axios.post(`${API}/recent-tickers`, {
          session_id: sessionId,
          ticker: upperTicker
        });
        
        // Update local state optimistically
        setRecentTickers(prev => {
          const filtered = prev.filter(t => t !== upperTicker);
          const updated = [upperTicker, ...filtered];
          return updated.slice(0, 8);
        });
      } catch (error) {
        console.error('Failed to save recent ticker:', error);
        // Update local state anyway
        setRecentTickers(prev => {
          const filtered = prev.filter(t => t !== upperTicker);
          const updated = [upperTicker, ...filtered];
          return updated.slice(0, 8);
        });
      }
    }
  };

  // Handle URL parameters (from AI Screener navigation)
  useEffect(() => {
    const tickerParam = searchParams.get('ticker');
    const tabParam = searchParams.get('tab');
    
    if (tickerParam) {
      setTicker(hydrateMarketFromTicker(tickerParam));
      if (tabParam) {
        setActiveTab(tabParam);
      }
      // Trigger analysis after a short delay
      setTimeout(() => handleAnalyze(), 500);
    }
  }, [searchParams]);

  // No auto-analyze on mount — user picks a stock first

  useEffect(() => {
    if (dataSource === 'polygon') {
      setDataSource('yahoo');
    }
  }, [dataSource]);



  const getTimeframeDays = (timeframe) => {
    switch (timeframe) {
      case '1d': return 1;
      case '5d': return 5;
      case '10d': return 10;
      case '20d': return 20;
      case '3m': return 90;
      case 'ytd': return Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24));
      case '1y': return 365;
      default: return 90;
    }
  };

  const normalizeTickerForMarket = (rawTicker, overrideRegion = null, overrideExchange = null) => {
    const region = overrideRegion || marketRegion;
    const exchange = overrideExchange || indiaExchange;
    const upperTicker = (rawTicker || '').trim().toUpperCase();
    if (!upperTicker) return '';

    // Preserve explicit exchange suffixes regardless of selected region.
    if (upperTicker.endsWith('.NS') || upperTicker.endsWith('.BO')) return upperTicker;
    if (region !== 'india') return upperTicker;
    return `${upperTicker}${exchange === 'BSE' ? '.BO' : '.NS'}`;
  };

  const hydrateMarketFromTicker = (rawTicker) => {
    const upperTicker = (rawTicker || '').trim().toUpperCase();
    if (upperTicker.endsWith('.NS')) {
      setMarketRegion('india');
      setIndiaExchange('NSE');
      return upperTicker.replace('.NS', '');
    }
    if (upperTicker.endsWith('.BO')) {
      setMarketRegion('india');
      setIndiaExchange('BSE');
      return upperTicker.replace('.BO', '');
    }
    return upperTicker;
  };

  const stripExchangeSuffix = (rawTicker) => {
    const upperTicker = (rawTicker || '').toUpperCase();
    return upperTicker.replace('.NS', '').replace('.BO', '');
  };

  const fetchChartData = async (selectedTimeframe = chartTimeframe, tickerOverride = null) => {
    try {
      const symbol = normalizeTickerForMarket(tickerOverride || ticker);
      if (!symbol) return;
      
      const days = getTimeframeDays(selectedTimeframe);
      console.log(`Fetching chart data for ${symbol} with ${days} days (${selectedTimeframe})`);
      
      // Get basic chart data
      const chartResponse = await axios.get(`${API}/chart-data/${symbol}?days=${days}`);
      const processedData = chartResponse.data.data.map(item => ({
        date: new Date(item.date).toLocaleDateString('en-US'),
        close: item.close,
        high: item.high,
        low: item.low,
        open: item.open,
        volume: item.volume
      }));
      setChartData(processedData);
      console.log(`Updated chartData with ${processedData.length} data points`);

      // Get chart data with indicators
      const indicatorResponse = await axios.get(`${API}/chart-data-with-indicators/${symbol}?days=${days}`);
      setChartDataWithIndicators(indicatorResponse.data.data);
      console.log(`Updated chartDataWithIndicators with ${indicatorResponse.data.data.length} data points`);
      
    } catch (error) {
      console.error('Error fetching chart data:', error);
      // Don't clear the data on error, keep existing data
    }
  };

  const handleTickerSelect = async (selectedTicker) => {
    const cleanedTicker = hydrateMarketFromTicker(selectedTicker);
    const normalizedTicker = normalizeTickerForMarket(cleanedTicker);
    setTicker(cleanedTicker);
    setError(null);
    setAnalysis(null);
    setChartData([]);
    setChartDataWithIndicators([]);
    setCompanyInfo(null);
    
    // Trigger analysis for the selected ticker
    try {
      setLoading(true);
      const response = await axios.post(`${API}/analyze`, { ticker: normalizedTicker });
      setAnalysis(response.data.analysis);
      setCompanyInfo({
        name: response.data.analysis.company_name,
        marketCap: response.data.analysis.market_cap
      });

      // Add ticker to recent tickers
      await addToRecentTickers(normalizedTicker);

      await fetchChartData(chartTimeframe, cleanedTicker);
    } catch (error) {
      console.error('Error analyzing selected ticker:', error);
      if (error.response) {
        if (error.response.status === 400) {
          setError(`No data found for ticker "${selectedTicker}". Please verify the ticker symbol and try again.`);
        } else if (error.response.status === 500) {
          setError(`Unable to analyze "${selectedTicker}" at this time. This could be due to an invalid ticker symbol or data unavailability.`);
        } else {
          setError(`Error analyzing "${selectedTicker}": ${error.response.data?.detail || 'Unknown error occurred'}`);
        }
      } else {
        setError(`Unexpected error occurred while analyzing "${selectedTicker}". Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async (overrideTicker) => {
    const normalizedTicker = normalizeTickerForMarket(overrideTicker || ticker);
    setLoading(true);
    setError(null); // Clear previous errors
    setAnalysis(null); // Clear previous analysis
    setChartData([]); // Clear previous chart data
    
    try {
      // Perform analysis with selected data source
      const response = await axios.get(`${API}/analyze/${normalizedTicker}?data_source=${dataSource}`);
      setAnalysis(response.data.analysis);
      
      // Set company info
      setCompanyInfo({
        name: response.data.analysis.company_name,
        marketCap: response.data.analysis.market_cap
      });

      // Add ticker to recent tickers
      await addToRecentTickers(normalizedTicker);

      // Fetch chart data and earnings after successful analysis
      await Promise.all([
        fetchChartData(chartTimeframe, ticker),
        fetchEarningsData(ticker)
      ]);
    } catch (error) {
      console.error('Error analyzing ticker:', error);
      
      // Set user-friendly error message
      if (error.response) {
        if (error.response.status === 400) {
          setError(`No data found for ticker "${normalizedTicker}". Please verify the ticker symbol and try again.`);
        } else if (error.response.status === 500) {
          setError(`Unable to analyze "${normalizedTicker}" at this time. This could be due to an invalid ticker symbol or data unavailability.`);
        } else {
          setError(`Error analyzing "${normalizedTicker}": ${error.response.data?.detail || 'Unknown error occurred'}`);
        }
      } else if (error.request) {
        setError('Unable to connect to the analysis service. Please check your internet connection and try again.');
      } else {
        setError(`Unexpected error occurred while analyzing "${normalizedTicker}". Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMag7Data = async (selectedTimeframe = mag7Timeframe) => {
    try {
      setMag7Loading(true);
      console.log(`Fetching MAG7 data for timeframe: ${selectedTimeframe}`);
      
      const response = await axios.get(`${API}/mag7-data?period=${selectedTimeframe}`);
      setMag7Data(response.data.data);
      console.log(`Updated MAG7 data for ${Object.keys(response.data.data).length} stocks`);
      
    } catch (error) {
      console.error('Error fetching MAG7 data:', error);
    } finally {
      setMag7Loading(false);
    }
  };

  const fetchOptionsStrategies = async (risk = riskLevel, prediction = userPrediction) => {
    if (marketRegion === 'india') {
      setOptionsData(null);
      return;
    }
    const symbol = normalizeTickerForMarket(ticker);
    if (!symbol) return;
    setOptionsLoading(true);
    try {
      const url = `${API}/options-strategies/${symbol}?risk_level=${risk}${prediction !== 'Auto' ? `&user_trend=${prediction}` : ''}`;
      const response = await axios.get(url);
      setOptionsData(response.data.data);
    } catch (error) {
      console.error('Error fetching options strategies:', error);
      setOptionsData(null);
    } finally {
      setOptionsLoading(false);
    }
  };

  const fetchNews = async () => {
    const symbol = normalizeTickerForMarket(ticker);
    if (!symbol) return;
    setNewsLoading(true);
    try {
      const response = await axios.get(`${API}/news/${symbol}?limit=10`);
      setNewsData(response.data.data.news);
      // Auto-select first news item
      if (response.data.data.news.length > 0) {
        setSelectedNews(response.data.data.news[0]);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setNewsData([]);
    } finally {
      setNewsLoading(false);
    }
  };

  const fetchMultiTimeframeData = async () => {
    const symbol = normalizeTickerForMarket(ticker);
    if (!symbol) return;
    setLoading(true);
    try {
      // Fetch data for all 4 timeframes in parallel
      const timeframes = ['1h', '4h', '1d', '1w'];
      const promises = timeframes.map(tf => 
        axios.get(`${API}/chart-data-with-indicators/${symbol}?timeframe=${tf}&data_source=${dataSource}`)
      );
      
      const responses = await Promise.all(promises);
      
      const newData = {};
      timeframes.forEach((tf, idx) => {
        newData[tf] = responses[idx].data.data || [];
      });
      
      setMultiChartData(newData);
    } catch (error) {
      console.error('Error fetching multi-timeframe data:', error);
      setMultiChartData({
        '1h': [],
        '4h': [],
        '1d': [],
        '1w': []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEarningsData = async (tickerOverride = null) => {
    const symbol = normalizeTickerForMarket(tickerOverride || ticker);
    if (!symbol) return;
    setEarningsLoading(true);
    try {
      const response = await axios.get(`${API}/earnings/${symbol}`);
      setEarningsData(response.data.data);
    } catch (error) {
      console.error('Error fetching earnings data:', error);
      setEarningsData(null);
    } finally {
      setEarningsLoading(false);
    }
  };

  const formatMarketCap = (marketCap) => {
    if (!marketCap) return '';
    
    if (marketCap >= 1e12) {
      return `${(marketCap / 1e12).toFixed(2)}T`;
    } else if (marketCap >= 1e9) {
      return `${(marketCap / 1e9).toFixed(2)}B`;
    } else if (marketCap >= 1e6) {
      return `${(marketCap / 1e6).toFixed(2)}M`;
    } else {
      return `${marketCap.toLocaleString('en-US')}`;
    }
  };

  const getCurrentSupportResistance = () => {
    if (!analysis) return { support: [], resistance: [] };
    
    switch (supportResistanceTimeframe) {
      case 'today':
        return { 
          support: analysis.support_levels_today || [], 
          resistance: analysis.resistance_levels_today || [] 
        };
      case 'tomorrow':
        return { 
          support: analysis.support_levels_tomorrow || [], 
          resistance: analysis.resistance_levels_tomorrow || [] 
        };
      case 'this_friday':
        return { 
          support: analysis.support_levels_this_friday || [], 
          resistance: analysis.resistance_levels_this_friday || [] 
        };
      case 'next_friday':
        return { 
          support: analysis.support_levels_next_friday || [], 
          resistance: analysis.resistance_levels_next_friday || [] 
        };
      case 'month_end':
        return { 
          support: analysis.support_levels_month_end || [], 
          resistance: analysis.resistance_levels_month_end || [] 
        };
      default:
        return { 
          support: analysis.support_levels_today || [], 
          resistance: analysis.resistance_levels_today || [] 
        };
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'Bullish':
        return <TrendingUp className="w-4 h-4 text-emerald-500" />;
      case 'Bearish':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'Bullish':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
      case 'Bearish':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'Positive':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
      case 'Negative':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="animate-fade-in">
        {/* Sticky Analyzer Header */}
        <div className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-background/80 backdrop-blur-xl border-b border-border/40 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Input
                placeholder="Ticker (e.g., RELIANCE, TCS)"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                className="w-44 h-9 text-sm bg-secondary/50 border-border focus:border-emerald-500 focus:ring-emerald-500/20"
                onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
              />
              <Select value={indiaExchange} onValueChange={setIndiaExchange}>
                <SelectTrigger className="w-20 h-9 text-sm bg-secondary/50 border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NSE">NSE</SelectItem>
                  <SelectItem value="BSE">BSE</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={handleAnalyze} 
                disabled={loading}
                className="h-9 text-sm bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
              >
                {loading ? 'Analyzing...' : 'Analyze'}
              </Button>
            </div>

            {analysis && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/60 border border-border/50">
                <span className="text-xs font-semibold text-muted-foreground">
                  {normalizeTickerForMarket(ticker) || 'SPY'}
                </span>
                <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                  {analysis.current_price?.toFixed(2) || '0.00'}
                </span>
                {companyInfo && (
                  <span className="text-xs text-muted-foreground hidden lg:inline">
                    {companyInfo.name}
                    {companyInfo.marketCap && ` (${formatMarketCap(companyInfo.marketCap)})`}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Stock Search */}
          <div className="relative mt-2" ref={indiaSearchRef}>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search stocks — company name, NSE/BSE symbol, or ISIN..."
                  value={indiaSearchQuery}
                  onChange={(e) => { setIndiaSearchQuery(e.target.value); }}
                  onFocus={() => { if (indiaSearchResults.length > 0) setIndiaSearchOpen(true); }}
                  className="h-9 text-sm pl-8 bg-secondary/50 border-border focus:border-emerald-500 focus:ring-emerald-500/20"
                />
                {indiaSearchQuery && (
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => { setIndiaSearchQuery(''); setIndiaSearchResults([]); setIndiaSearchOpen(false); }}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {indiaSearchOpen && indiaSearchResults.length > 0 && (
                <div className="absolute z-[100] top-10 left-0 w-full max-h-80 overflow-y-auto bg-popover border border-border rounded-xl shadow-2xl">
                  {indiaSearchResults.map((s) => (
                    <button
                      key={s.isin || s.nse_symbol}
                      className="block w-full text-left px-3 py-2.5 hover:bg-secondary border-b border-border/50 last:border-0 transition-colors overflow-hidden"
                      onClick={() => {
                        const sym = s.nse_symbol || s.bse_code;
                        setTicker(sym);
                        setIndiaSearchQuery('');
                        setIndiaSearchResults([]);
                        setIndiaSearchOpen(false);
                        handleAnalyze(sym);
                      }}
                    >
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 leading-snug">{s.nse_symbol || s.bse_code}</p>
                      <p className="text-sm text-foreground truncate leading-snug">{s.company_name}</p>
                      {s.ai_industry && (
                        <p className="text-[10px] text-muted-foreground truncate leading-snug">{s.ai_industry}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

          {/* Quick tickers + recents */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {commonTickers.map((commonTicker) => (
              <button
                key={commonTicker}
                onClick={() => handleTickerSelect(commonTicker)}
                disabled={loading}
                className={`h-6 px-2.5 text-[11px] font-medium rounded-md transition-all ${
                  stripExchangeSuffix(ticker) === commonTicker
                    ? 'bg-emerald-600 text-white'
                    : 'bg-secondary/60 text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                {commonTicker}
              </button>
            ))}
            {recentTickers.length > 0 && <div className="w-px h-6 bg-border mx-1 self-center" />}
            {recentTickers.map((recentTicker) => (
              <button
                key={recentTicker}
                onClick={() => handleTickerSelect(recentTicker)}
                disabled={loading}
                className="h-6 px-2.5 text-[11px] font-medium rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-all"
              >
                {stripExchangeSuffix(recentTicker)}
              </button>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <Alert className="mb-6 border-destructive/30 bg-destructive/5">
            <AlertDescription className="text-destructive">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="inline-flex h-9 items-center justify-center rounded-lg bg-secondary p-1 text-muted-foreground">
            <TabsTrigger value="analysis" className="rounded-md px-3 py-1 text-xs font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">
              Technical Analysis
            </TabsTrigger>
            <TabsTrigger value="news" className="rounded-md px-3 py-1 text-xs font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm" onClick={() => ticker && fetchNews()}>
              News
            </TabsTrigger>
            <TabsTrigger value="charts" className="rounded-md px-3 py-1 text-xs font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm" onClick={() => ticker && fetchMultiTimeframeData()}>
              Charts
            </TabsTrigger>
          </TabsList>

          {/* Technical Analysis Tab */}
          <TabsContent value="analysis" className="space-y-6">
            {analysis && (
              <>
                {/* Technical Indicators - Single Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-1.5">
                  <Card className="p-1.5">
                    <div className="text-center">
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">Current Price</div>
                      <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                        {analysis.current_price?.toFixed(2)}
                      </div>
                    </div>
                  </Card>

                  <Card className="p-1.5">
                    <div className="text-center">
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">BB Upper</div>
                      <div className="text-sm font-bold">
                        {analysis.bollinger_upper?.toFixed(2) || 'N/A'}
                      </div>
                    </div>
                  </Card>

                  <Card className="p-1.5">
                    <div className="text-center">
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">BB Lower</div>
                      <div className="text-sm font-bold">
                        {analysis.bollinger_lower?.toFixed(2) || 'N/A'}
                      </div>
                    </div>
                  </Card>

                  <Card className="p-1.5">
                    <div className="text-center">
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">CCI</div>
                      <div className={`text-sm font-bold ${
                        analysis.cci > 100 ? 'text-red-600 dark:text-red-500' : 
                        analysis.cci < -100 ? 'text-green-600 dark:text-green-500' : 
                        'text-gray-900 dark:text-gray-100'
                      }`}>
                        {analysis.cci?.toFixed(2) || 'N/A'}
                      </div>
                      {analysis.cci > 100 && (
                        <div className="text-[9px] text-red-600 dark:text-red-500">SELL</div>
                      )}
                      {analysis.cci < -100 && (
                        <div className="text-[9px] text-green-600 dark:text-green-500">BUY</div>
                      )}
                    </div>
                  </Card>

                  <Card className="p-1.5">
                    <div className="text-center">
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">RSI</div>
                      <div className={`text-sm font-bold ${
                        analysis.rsi > 70 ? 'text-red-600 dark:text-red-500' : 
                        analysis.rsi < 30 ? 'text-green-600 dark:text-green-500' : 
                        'text-gray-900 dark:text-gray-100'
                      }`}>
                        {analysis.rsi?.toFixed(1)}
                      </div>
                      {analysis.rsi > 70 && (
                        <div className="text-[9px] text-red-600 dark:text-red-500">OVERBOUGHT</div>
                      )}
                      {analysis.rsi < 30 && (
                        <div className="text-[9px] text-green-600 dark:text-green-500">OVERSOLD</div>
                      )}
                    </div>
                  </Card>

                  <Card className="p-1.5">
                    <div className="text-center">
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">SMA 20</div>
                      <div className="text-sm font-bold">
                        {analysis.sma_20?.toFixed(2)}
                      </div>
                    </div>
                  </Card>

                  <Card className="p-1.5">
                    <div className="text-center">
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">SMA 50</div>
                      <div className="text-sm font-bold">
                        {analysis.sma_50?.toFixed(2) || 'N/A'}
                      </div>
                    </div>
                  </Card>

                  <Card className="p-1.5">
                    <div className="text-center">
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">SMA 200</div>
                      <div className="text-sm font-bold">
                        {analysis.sma_200?.toFixed(2) || 'N/A'}
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Next Earnings */}
                {earningsLoading ? (
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                        <span className="text-sm">Loading earnings data...</span>
                      </div>
                    </CardContent>
                  </Card>
                ) : earningsData?.next_earnings ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="w-5 h-5" />
                        Next Earnings
                      </CardTitle>
                      <CardDescription>Upcoming earnings announcement date</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg border-l-4 border-emerald-600">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Earnings Date</div>
                            <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                              {new Date(earningsData.next_earnings.date).toLocaleDateString('en-US', { 
                                month: 'long', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                              {Math.ceil((new Date(earningsData.next_earnings.date) - new Date()) / (1000 * 60 * 60 * 24))} days away
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : null}

                {/* Trend Analysis */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle>Trend Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div>
                          <div className="font-medium">5-Day Trend</div>
                          <div className="text-sm text-gray-500">Short-term</div>
                          <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                            SMA: {analysis.sma_5?.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getTrendIcon(analysis.trend_5d)}
                          <Badge className={getTrendColor(analysis.trend_5d)}>
                            {analysis.trend_5d}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div>
                          <div className="font-medium">30-Day Trend</div>
                          <div className="text-sm text-gray-500">Medium-term</div>
                          <div className="text-lg font-bold text-blue-600 dark:text-blue-400 mt-1">
                            SMA: {analysis.sma_30?.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getTrendIcon(analysis.trend_30d)}
                          <Badge className={getTrendColor(analysis.trend_30d)}>
                            {analysis.trend_30d}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div>
                          <div className="font-medium">60-Day Trend</div>
                          <div className="text-sm text-gray-500">Long-term</div>
                          <div className="text-lg font-bold text-purple-600 dark:text-purple-400 mt-1">
                            SMA: {analysis.sma_60?.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getTrendIcon(analysis.trend_60d)}
                          <Badge className={getTrendColor(analysis.trend_60d)}>
                            {analysis.trend_60d}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div>
                          <div className="font-medium">1-Year Trend</div>
                          <div className="text-sm text-gray-500">Very long-term</div>
                          <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400 mt-1">
                            252-Day Analysis
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getTrendIcon(analysis.trend_1y)}
                          <Badge className={getTrendColor(analysis.trend_1y)}>
                            {analysis.trend_1y}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Support & Resistance + Sentiment */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Support & Resistance Levels</CardTitle>
                          <CardDescription>Key price levels to watch</CardDescription>
                        </div>
                        <Select value={supportResistanceTimeframe} onValueChange={setSupportResistanceTimeframe}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Select timeframe" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="today">Today</SelectItem>
                            <SelectItem value="tomorrow">Tomorrow</SelectItem>
                            <SelectItem value="this_friday">This Friday</SelectItem>
                            <SelectItem value="next_friday">Next Friday</SelectItem>
                            <SelectItem value="month_end">Month End</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium text-red-600 dark:text-red-400">Resistance Levels</Label>
                        <div className="mt-2 space-y-1">
                          {getCurrentSupportResistance().resistance.map((level, index) => (
                            <div key={index} className="flex justify-between items-center p-2 bg-red-50 dark:bg-red-950 rounded">
                              <span className="text-sm">Level {index + 1}</span>
                              <span className="font-medium">{level.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Support Levels</Label>
                        <div className="mt-2 space-y-1">
                          {getCurrentSupportResistance().support.map((level, index) => (
                            <div key={index} className="flex justify-between items-center p-2 bg-emerald-50 dark:bg-emerald-950 rounded">
                              <span className="text-sm">Level {index + 1}</span>
                              <span className="font-medium">{level.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Market Sentiment & Macro Indicators</CardTitle>
                      <CardDescription>Market sentiment, Fear & Greed, Shiller PE, and correlations</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {/* Market Sentiment */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-medium">Market Sentiment</span>
                            <Badge className={getSentimentColor(analysis.sentiment_label)}>
                              {analysis.sentiment_label}
                            </Badge>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                            <div 
                              className={`h-2 rounded-full transition-all duration-300 ${
                                analysis.sentiment_score > 0 ? 'bg-emerald-500' : 'bg-red-500'
                              }`}
                              style={{ 
                                width: `${Math.abs(analysis.sentiment_score) * 50 + 50}%`,
                                marginLeft: analysis.sentiment_score < 0 ? `${(1 + analysis.sentiment_score) * 50}%` : '0'
                              }}
                            />
                          </div>
                        </div>

                        {/* Fear & Greed Index */}
                        {analysis.fear_greed_index && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium">CNN Fear & Greed Index</span>
                              <Badge className={
                                analysis.fear_greed_label === 'Extreme Greed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                analysis.fear_greed_label === 'Greed' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                                analysis.fear_greed_label === 'Neutral' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' :
                                analysis.fear_greed_label === 'Fear' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              }>
                                {analysis.fear_greed_label} ({analysis.fear_greed_index.toFixed(0)})
                              </Badge>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  analysis.fear_greed_index > 75 ? 'bg-red-500' :
                                  analysis.fear_greed_index > 55 ? 'bg-orange-500' :
                                  analysis.fear_greed_index > 45 ? 'bg-gray-500' :
                                  analysis.fear_greed_index > 25 ? 'bg-yellow-500' : 'bg-green-500'
                                }`}
                                style={{ width: `${analysis.fear_greed_index}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Shiller PE */}
                        {analysis.shiller_pe && (
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Shiller PE (CAPE)</span>
                            <span className="text-lg font-bold">{analysis.shiller_pe.toFixed(1)}</span>
                          </div>
                        )}

                        {/* Correlations */}
                        <div>
                          <div className="font-medium mb-3">Commodity & Dollar Correlations (90 days)</div>
                          <div className="grid grid-cols-2 gap-3">
                            {analysis.oil_correlation !== null && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                                <span className="text-sm">🛢️ Oil</span>
                                <span className={`font-medium ${
                                  analysis.oil_correlation > 0.3 ? 'text-emerald-600' :
                                  analysis.oil_correlation < -0.3 ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {analysis.oil_correlation > 0 ? '+' : ''}{(analysis.oil_correlation * 100).toFixed(0)}%
                                </span>
                              </div>
                            )}
                            
                            {analysis.gold_correlation !== null && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                                <span className="text-sm">🥇 Gold</span>
                                <span className={`font-medium ${
                                  analysis.gold_correlation > 0.3 ? 'text-emerald-600' :
                                  analysis.gold_correlation < -0.3 ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {analysis.gold_correlation > 0 ? '+' : ''}{(analysis.gold_correlation * 100).toFixed(0)}%
                                </span>
                              </div>
                            )}
                            
                            {analysis.silver_correlation !== null && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                                <span className="text-sm">🥈 Silver</span>
                                <span className={`font-medium ${
                                  analysis.silver_correlation > 0.3 ? 'text-emerald-600' :
                                  analysis.silver_correlation < -0.3 ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {analysis.silver_correlation > 0 ? '+' : ''}{(analysis.silver_correlation * 100).toFixed(0)}%
                                </span>
                              </div>
                            )}
                            
                            {analysis.dollar_correlation !== null && (
                              <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                                <span className="text-sm">💵 USD</span>
                                <span className={`font-medium ${
                                  analysis.dollar_correlation > 0.3 ? 'text-emerald-600' :
                                  analysis.dollar_correlation < -0.3 ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {analysis.dollar_correlation > 0 ? '+' : ''}{(analysis.dollar_correlation * 100).toFixed(0)}%
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <Alert>
                          <AlertDescription>
                            Macro indicators provide market context. Fear & Greed Index simulated. High correlations (&gt;30%) suggest strong relationships.
                          </AlertDescription>
                        </Alert>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Charts Tab */}
          <TabsContent value="charts" className="space-y-6">
            {!loading ? (
              <>
                {/* Volume Toggle */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>Multi-Timeframe Analysis - {ticker}</CardTitle>
                        <CardDescription>View price action across 4 different timeframes simultaneously</CardDescription>
                      </div>
                      <div className="flex items-center gap-3">
                        <Label className="text-sm font-medium">Show Volume</Label>
                        <Switch
                          checked={showVolume}
                          onCheckedChange={setShowVolume}
                          className="data-[state=checked]:bg-emerald-600"
                        />
                        <Button 
                          onClick={fetchMultiTimeframeData}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          Refresh Charts
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* 4 Charts Grid - 2x2 Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* 1 Hour Chart */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">1 Hour</CardTitle>
                      <CardDescription className="text-xs">Short-term intraday movements</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {multiChartData['1h'].length > 0 ? (
                        <CandlestickChart
                          key={`1h-${showVolume}`}
                          data={multiChartData['1h']}
                          indicators={{
                            sma5: true,
                            sma50: true,
                            sma100: false,
                            sma200: false,
                            bollinger: true,
                            volume: showVolume
                          }}
                          height={400}
                          darkMode={darkMode}
                        />
                      ) : (
                        <div className="h-[400px] flex items-center justify-center text-gray-500">
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* 4 Hour Chart */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">4 Hour</CardTitle>
                      <CardDescription className="text-xs">Medium-term intraday trends</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {multiChartData['4h'].length > 0 ? (
                        <CandlestickChart
                          key={`4h-${showVolume}`}
                          data={multiChartData['4h']}
                          indicators={{
                            sma5: true,
                            sma50: true,
                            sma100: true,
                            sma200: false,
                            bollinger: true,
                            volume: showVolume
                          }}
                          height={400}
                          darkMode={darkMode}
                        />
                      ) : (
                        <div className="h-[400px] flex items-center justify-center text-gray-500">
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* 1 Day Chart */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">1 Day</CardTitle>
                      <CardDescription className="text-xs">Daily price movements</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {multiChartData['1d'].length > 0 ? (
                        <CandlestickChart
                          key={`1d-${showVolume}`}
                          data={multiChartData['1d']}
                          indicators={{
                            sma5: true,
                            sma50: true,
                            sma100: true,
                            sma200: true,
                            bollinger: true,
                            volume: showVolume
                          }}
                          height={400}
                          darkMode={darkMode}
                        />
                      ) : (
                        <div className="h-[400px] flex items-center justify-center text-gray-500">
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* 1 Week Chart */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">1 Week</CardTitle>
                      <CardDescription className="text-xs">Long-term weekly trends</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {multiChartData['1w'].length > 0 ? (
                        <CandlestickChart
                          key={`1w-${showVolume}`}
                          data={multiChartData['1w']}
                          indicators={{
                            sma5: true,
                            sma50: true,
                            sma100: true,
                            sma200: true,
                            bollinger: true,
                            volume: showVolume
                          }}
                          height={400}
                          darkMode={darkMode}
                        />
                      ) : (
                        <div className="h-[400px] flex items-center justify-center text-gray-500">
                          No data available
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Indicator Charts Grid */}
                <Card>
                  <CardHeader>
                    <CardTitle>Technical Oscillators & Volume Indicators</CardTitle>
                    <CardDescription>RSI, MACD, MFI, OBV, A/D Line, and CMF</CardDescription>
                  </CardHeader>
                </Card>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* RSI Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">RSI (14-period)</CardTitle>
                      <CardDescription className="text-xs">Momentum oscillator (0-100)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <OscillatorChart
                        data={chartDataWithIndicators}
                        series={[
                          { key: 'rsi', color: '#f59e0b', lineWidth: 2, name: 'RSI' }
                        ]}
                        horizontalLines={[
                          { value: 70, color: '#ef4444', style: 2, width: 1 },
                          { value: 30, color: '#10b981', style: 2, width: 1 }
                        ]}
                        height={250}
                        darkMode={darkMode}
                        yAxisDomain={[0, 100]}
                        title="RSI"
                      />
                    </CardContent>
                  </Card>

                  {/* MACD Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">MACD</CardTitle>
                      <CardDescription className="text-xs">Moving Average Convergence Divergence</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <OscillatorChart
                        data={chartDataWithIndicators}
                        series={[
                          { key: 'macd', color: '#3b82f6', lineWidth: 2, name: 'MACD' },
                          { key: 'macd_signal', color: '#ef4444', lineWidth: 2, name: 'Signal' }
                        ]}
                        horizontalLines={[
                          { value: 0, color: darkMode ? '#6b7280' : '#9ca3af', style: 2, width: 1 }
                        ]}
                        height={250}
                        darkMode={darkMode}
                        title="MACD"
                      />
                    </CardContent>
                  </Card>

                  {/* Money Flow Index (MFI) Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Money Flow Index (MFI)</CardTitle>
                      <CardDescription className="text-xs">RSI with volume consideration (0-100)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <OscillatorChart
                        data={chartDataWithIndicators}
                        series={[
                          { key: 'mfi', color: '#f59e0b', lineWidth: 2, name: 'MFI' },
                          { key: 'rsi', color: '#6b7280', lineWidth: 1, name: 'RSI (ref)' }
                        ]}
                        horizontalLines={[
                          { value: 80, color: '#ef4444', style: 2, width: 1 },
                          { value: 20, color: '#10b981', style: 2, width: 1 }
                        ]}
                        height={250}
                        darkMode={darkMode}
                        yAxisDomain={[0, 100]}
                        title="MFI"
                      />
                    </CardContent>
                  </Card>

                  {/* OBV Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">On Balance Volume (OBV)</CardTitle>
                      <CardDescription className="text-xs">Volume flow analysis</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <OscillatorChart
                        data={chartDataWithIndicators}
                        series={[
                          { key: 'obv', color: '#3b82f6', lineWidth: 2, name: 'OBV' }
                        ]}
                        horizontalLines={[]}
                        height={250}
                        darkMode={darkMode}
                        title="OBV"
                      />
                    </CardContent>
                  </Card>

                  {/* A/D Line Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Accumulation/Distribution Line</CardTitle>
                      <CardDescription className="text-xs">Buying/selling pressure</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <OscillatorChart
                        data={chartDataWithIndicators}
                        series={[
                          { key: 'ad_line', color: '#f59e0b', lineWidth: 2, name: 'A/D Line' }
                        ]}
                        horizontalLines={[]}
                        height={250}
                        darkMode={darkMode}
                        title="AD"
                      />
                    </CardContent>
                  </Card>

                  {/* Chaikin Money Flow (CMF) Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Chaikin Money Flow</CardTitle>
                      <CardDescription className="text-xs">Buying/selling pressure analysis</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <OscillatorChart
                        data={chartDataWithIndicators}
                        series={[
                          { key: 'cmf', color: '#8b5cf6', lineWidth: 2, name: 'CMF' }
                        ]}
                        horizontalLines={[
                          { value: 0.2, color: '#10b981', style: 2, width: 1 },
                          { value: 0, color: '#6b7280', style: 3, width: 1 },
                          { value: -0.2, color: '#ef4444', style: 2, width: 1 }
                        ]}
                        height={250}
                        darkMode={darkMode}
                        yAxisDomain={[-1, 1]}
                        title="CMF"
                      />
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : loading ? (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Loading Charts - {ticker}</CardTitle>
                      <CardDescription>Fetching data for {chartTimeframe} timeframe...</CardDescription>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Timeframe:</Label>
                        <Select value={chartTimeframe} disabled>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-96 flex items-center justify-center">
                    <div className="text-center space-y-4">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
                      <div className="font-medium">Loading chart data...</div>
                      <div className="text-sm text-gray-500">Please wait while we fetch {chartTimeframe} data</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Multi-Timeframe Charts - {ticker}</CardTitle>
                      <CardDescription>Comprehensive technical analysis with multiple indicators</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Chart Type:</Label>
                      <Button
                        variant={chartType === 'recharts' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setChartType('recharts')}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        Recharts
                      </Button>
                      <Button
                        variant={chartType === 'tradingview' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setChartType('tradingview')}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        TradingView
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-96 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <BarChart3 className="w-12 h-12 mx-auto mb-2" />
                      <div>No chart data available</div>
                      <div className="text-sm">Analyze a ticker to view comprehensive charts</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Options Tab */}
          <TabsContent value="options" className="space-y-6">
            {marketRegion === 'india' ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-gray-500">
                    <div className="text-lg font-medium mb-2">Options Strategies (US only)</div>
                    <div className="text-sm">Switch market to US to use options strategies, or continue with India for technical/news/charts analysis.</div>
                  </div>
                </CardContent>
              </Card>
            ) : !ticker ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-gray-500">
                    <div className="text-lg font-medium mb-2">No Ticker Selected</div>
                    <div className="text-sm">Please enter a ticker symbol above to view options strategies</div>
                  </div>
                </CardContent>
              </Card>
            ) : optionsLoading ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-gray-500">
                    <div className="text-lg font-medium">Loading options strategies...</div>
                  </div>
                </CardContent>
              </Card>
            ) : optionsData ? (
              <>
                {/* Header with VIX and Controls */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>Options Strategies - {optionsData.ticker}</CardTitle>
                        <CardDescription>
                          Current Price: {optionsData.current_price.toFixed(2)}
                          {optionsData.detected_trend && !optionsData.ai_prediction && (
                            <span className="ml-3">
                              Detected Trend: <span className={`font-semibold ${
                                optionsData.detected_trend === 'Bullish' ? 'text-green-600' :
                                optionsData.detected_trend === 'Bearish' ? 'text-red-600' :
                                'text-blue-600'
                              }`}>{optionsData.detected_trend}</span>
                            </span>
                          )}
                          {optionsData.user_prediction && optionsData.user_prediction !== 'AI' && (
                            <span className="ml-3 text-purple-600 font-semibold">
                              (Using Your Prediction: {optionsData.user_prediction})
                            </span>
                          )}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-gray-500">VIX Index</div>
                          <div className={`text-2xl font-bold ${
                            optionsData.vix > 25 ? 'text-red-600' : 
                            optionsData.vix > 15 ? 'text-yellow-600' : 
                            'text-green-600'
                          }`}>
                            {optionsData.vix}
                          </div>
                          <div className="text-xs text-gray-500">
                            {optionsData.vix > 25 ? 'High Volatility' : 
                             optionsData.vix > 15 ? 'Moderate Volatility' : 
                             'Low Volatility'}
                          </div>
                        </div>
                        <div className="border-l pl-4">
                          <Label className="text-sm mb-2 block">Your Prediction</Label>
                          <Select value={userPrediction} onValueChange={async (value) => {
                            setUserPrediction(value);
                            await fetchOptionsStrategies(riskLevel, value);
                          }}>
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Auto">Auto Detect</SelectItem>
                              <SelectItem value="AI">🤖 AI Analysis</SelectItem>
                              <SelectItem value="Bullish">Bullish</SelectItem>
                              <SelectItem value="Bearish">Bearish</SelectItem>
                              <SelectItem value="Neutral">Neutral</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="border-l pl-4">
                          <Label className="text-sm mb-2 block">Risk Tolerance</Label>
                          <Select value={riskLevel} onValueChange={async (value) => {
                            setRiskLevel(value);
                            await fetchOptionsStrategies(value, userPrediction);
                          }}>
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Low">Low</SelectItem>
                              <SelectItem value="Mid">Mid</SelectItem>
                              <SelectItem value="High">High</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="border-l pl-4">
                          <Label className="text-sm mb-2 block">Strategy Type</Label>
                          <Select value={debitCreditFilter} onValueChange={async (value) => {
                            setDebitCreditFilter(value);
                            // Recalculate strategies when filter changes to ensure fresh data
                            await fetchOptionsStrategies(riskLevel, userPrediction);
                          }}>
                            <SelectTrigger className="w-36">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Either">Either</SelectItem>
                              <SelectItem value="Debit Only">Debit Only</SelectItem>
                              <SelectItem value="Credit Only">Credit Only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* AI Prediction Card */}
                {optionsData.ai_prediction && (
                  <Card className="border-2 border-purple-500 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-3xl">🤖</div>
                          <div>
                            <CardTitle className="text-lg">AI Trend Prediction</CardTitle>
                            <CardDescription>Based on comprehensive technical analysis</CardDescription>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-3xl font-bold ${
                            optionsData.ai_prediction === 'Bullish' ? 'text-green-600' :
                            optionsData.ai_prediction === 'Bearish' ? 'text-red-600' :
                            'text-blue-600'
                          }`}>
                            {optionsData.ai_prediction}
                          </div>
                          <Badge variant={
                            optionsData.ai_confidence === 'High' ? 'default' :
                            optionsData.ai_confidence === 'Medium' ? 'secondary' :
                            'outline'
                          } className="mt-1">
                            {optionsData.ai_confidence} Confidence
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          AI Analysis:
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {optionsData.ai_reasoning}
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
                        <span>📊</span>
                        <span>Analyzed: Price action, SMAs, RSI, MACD, Bollinger Bands, VIX, and recent momentum</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Strategies Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {optionsData.strategies
                    .filter(strategy => {
                      // Filter by debit/credit
                      if (debitCreditFilter === 'Debit Only') {
                        return strategy.net_cost >= 0; // Debit strategies (paying premium)
                      } else if (debitCreditFilter === 'Credit Only') {
                        return strategy.net_cost < 0; // Credit strategies (receiving premium)
                      }
                      return true; // Either - show all
                    })
                    .map((strategy, index) => (
                    <Card key={index} className={`border-l-4 ${
                      strategy.direction === 'Bullish' ? 'border-l-green-500' :
                      strategy.direction === 'Bearish' ? 'border-l-red-500' :
                      strategy.direction === 'Neutral' ? 'border-l-blue-500' :
                      'border-l-purple-500'
                    }`}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{strategy.name}</CardTitle>
                            <CardDescription>{strategy.type}</CardDescription>
                          </div>
                          <div className="flex gap-2">
                            <Badge variant={
                              strategy.direction === 'Bullish' ? 'default' :
                              strategy.direction === 'Bearish' ? 'destructive' :
                              'secondary'
                            }>
                              {strategy.direction}
                            </Badge>
                            <Badge variant="outline">{strategy.complexity}</Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {/* Trade Grid Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-gray-100 dark:bg-gray-800">
                                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">Expiration</th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">Call/Put</th>
                                  <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300">Strike</th>
                                  <th className="px-3 py-2 text-center font-semibold text-gray-700 dark:text-gray-300">Buy/Sell</th>
                                  <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300">Current Price</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {Object.entries(strategy.strikes).map(([key, strikeValue]) => {
                                  const isCall = key.toLowerCase().includes('call');
                                  const isPut = key.toLowerCase().includes('put');
                                  const isBuy = key.toLowerCase().includes('buy');
                                  const isSell = key.toLowerCase().includes('sell');
                                  const optionType = isCall ? 'Call' : isPut ? 'Put' : '-';
                                  const action = isBuy ? 'Buy' : isSell ? 'Sell' : '-';
                                  const currentPrice = strategy.prices && strategy.prices[key] ? Number(strategy.prices[key]).toFixed(2) : '-';
                                  
                                  return (
                                    <tr key={key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{strategy.expiration}</td>
                                      <td className="px-3 py-2">
                                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                          isCall ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                                          'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                        }`}>
                                          {optionType}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">
                                        {Number(strikeValue).toFixed(2)}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                                          isBuy ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                        }`}>
                                          {action}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-right font-bold text-gray-900 dark:text-gray-100">
                                        {currentPrice}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Single Row: Max Profit, Max Loss, Net Debit/Credit */}
                          <div className="grid grid-cols-3 gap-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                            <div className="text-center">
                              <div className="text-xs text-green-700 dark:text-green-400 mb-1">Max Profit</div>
                              <div className="text-lg font-bold text-green-700 dark:text-green-400">
                                {typeof strategy.max_profit === 'string' ? 
                                  strategy.max_profit : 
                                  Number(strategy.max_profit).toFixed(2)}
                              </div>
                            </div>
                            <div className="text-center border-x border-gray-300 dark:border-gray-600">
                              <div className="text-xs text-red-700 dark:text-red-400 mb-1">Max Loss</div>
                              <div className="text-lg font-bold text-red-700 dark:text-red-400">
                                {Number(strategy.max_loss).toFixed(2)}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Net {strategy.net_cost !== undefined && strategy.net_cost < 0 ? 'Credit' : 'Debit'}
                              </div>
                              <div className={`text-lg font-bold ${
                                strategy.net_cost !== undefined && strategy.net_cost < 0 ? 'text-emerald-600' : 'text-blue-600'
                              }`}>
                                {strategy.net_cost !== undefined ? Math.abs(strategy.net_cost).toFixed(2) : '0.00'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                  }
                  {optionsData.strategies.filter(strategy => {
                    if (debitCreditFilter === 'Debit Only') return strategy.net_cost >= 0;
                    if (debitCreditFilter === 'Credit Only') return strategy.net_cost < 0;
                    return true;
                  }).length === 0 && (
                    <div className="col-span-2 text-center py-12">
                      <div className="text-gray-500 text-lg">
                        No {debitCreditFilter === 'Debit Only' ? 'debit' : 'credit'} strategies available for current settings.
                        <div className="text-sm mt-2">Try adjusting Risk Tolerance or Your Prediction.</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Info Footer */}
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Note:</strong> These are suggested strategies based on current price action and technical analysis. 
                      Max profit/loss values are estimates. Always verify option prices and Greeks before placing trades. 
                      Options trading involves significant risk and is not suitable for all investors.
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-gray-500">
                    <div className="text-lg font-medium mb-2">No Data Available</div>
                    <div className="text-sm">Unable to load options strategies for {ticker}</div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* MAG7 + PLTR Tab */}
          <TabsContent value="mag7" className="space-y-6">
            {/* Controls Card */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>MAG7 + PLTR Analysis</CardTitle>
                    <CardDescription>Magnificent 7 stocks + Palantir with technical indicators</CardDescription>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Timeframe:</Label>
                      <Select value={mag7Timeframe} onValueChange={async (value) => {
                        console.log(`MAG7 timeframe changed to: ${value}`);
                        setMag7Timeframe(value);
                        await fetchMag7Data(value);
                      }}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1d">1 Day</SelectItem>
                          <SelectItem value="5d">5 Days</SelectItem>
                          <SelectItem value="1month">1 Month</SelectItem>
                          <SelectItem value="3months">3 Months</SelectItem>
                          <SelectItem value="6months">6 Months</SelectItem>
                          <SelectItem value="ytd">YTD</SelectItem>
                          <SelectItem value="1y">1 Year</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Grid:</Label>
                      <Select value={mag7GridLayout} onValueChange={setMag7GridLayout}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4x2">4×2</SelectItem>
                          <SelectItem value="3x3">3×3</SelectItem>
                          <SelectItem value="2x4">2×4</SelectItem>
                          <SelectItem value="1x8">1×8</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Chart Theme:</Label>
                      <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-1">
                        <Sun className="h-3 w-3" />
                        <Switch
                          checked={darkMode}
                          onCheckedChange={useThemeStore.getState().toggleTheme}
                          className="data-[state=checked]:bg-emerald-600"
                        />
                        <Moon className="h-3 w-3" />
                      </div>
                    </div>
                    <Button
                      onClick={() => fetchMag7Data()}
                      disabled={mag7Loading}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      size="sm"
                    >
                      {mag7Loading ? 'Loading...' : 'Refresh'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Indicators Toggle Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center gap-4">
                  <Label className="text-base font-semibold text-gray-900 dark:text-gray-100">Toggle Indicators:</Label>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="mag7-sma5"
                      checked={mag7Indicators.sma5}
                      onCheckedChange={(checked) => setMag7Indicators({...mag7Indicators, sma5: checked})}
                    />
                    <Label htmlFor="mag7-sma5" className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <div className="w-4 h-1 bg-yellow-400 rounded"></div>
                      5 SMA
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="mag7-sma50"
                      checked={mag7Indicators.sma50}
                      onCheckedChange={(checked) => setMag7Indicators({...mag7Indicators, sma50: checked})}
                    />
                    <Label htmlFor="mag7-sma50" className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <div className="w-4 h-1 bg-cyan-400 rounded"></div>
                      50 SMA
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="mag7-sma100"
                      checked={mag7Indicators.sma100}
                      onCheckedChange={(checked) => setMag7Indicators({...mag7Indicators, sma100: checked})}
                    />
                    <Label htmlFor="mag7-sma100" className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <div className="w-4 h-1 bg-purple-400 rounded"></div>
                      100 SMA
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="mag7-sma200"
                      checked={mag7Indicators.sma200}
                      onCheckedChange={(checked) => setMag7Indicators({...mag7Indicators, sma200: checked})}
                    />
                    <Label htmlFor="mag7-sma200" className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <div className="w-4 h-1 bg-orange-500 rounded"></div>
                      200 SMA
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="mag7-bollinger"
                      checked={mag7Indicators.bollinger}
                      onCheckedChange={(checked) => setMag7Indicators({...mag7Indicators, bollinger: checked})}
                    />
                    <Label htmlFor="mag7-bollinger" className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <div className="w-4 h-1 bg-red-400 rounded"></div>
                      Bollinger
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="mag7-volume"
                      checked={mag7Indicators.volume}
                      onCheckedChange={(checked) => setMag7Indicators({...mag7Indicators, volume: checked})}
                    />
                    <Label htmlFor="mag7-volume" className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <div className="w-4 h-1 bg-emerald-600 rounded"></div>
                      Volume
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {mag7Loading ? (
              <Card>
                <CardContent className="p-8">
                  <div className="flex items-center justify-center">
                    <div className="text-center space-y-4">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
                      <div className="font-medium">Loading MAG7 + PLTR data...</div>
                      <div className="text-sm text-gray-500">Fetching {mag7Timeframe} timeframe data</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : Object.keys(mag7Data).length > 0 ? (
              <div className={`grid gap-6 ${
                mag7GridLayout === '4x2' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
                mag7GridLayout === '3x3' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
                mag7GridLayout === '2x4' ? 'grid-cols-1 lg:grid-cols-2' :
                'grid-cols-1'
              }`}>
                {Object.entries(mag7Data).map(([ticker, data]) => {
                  // Determine ticker color based on price vs 5 SMA
                  let tickerColor = 'text-gray-600 dark:text-gray-400'; // default grey
                  if (data && data.sma_5 && data.current_price) {
                    if (data.current_price > data.sma_5) {
                      tickerColor = 'text-green-700 dark:text-green-600'; // above 5 SMA - dark green
                    } else if (data.current_price < data.sma_5) {
                      tickerColor = 'text-red-700 dark:text-red-600'; // below 5 SMA - dark red
                    }
                  }
                  
                  return data ? (
                    <Card key={ticker} className="overflow-hidden">
                      <CardHeader className="pb-2 px-3 pt-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className={`text-base font-bold ${tickerColor}`}>{ticker}</CardTitle>
                            <CardDescription className="text-xs">{data.company_name}</CardDescription>
                            {data.market_cap && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {formatMarketCap(data.market_cap)}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {/* Volume Indicator Arrow */}
                              {data.volume_above_avg !== undefined && (
                                <span className={`text-xs ${data.volume_above_avg ? 'text-green-600' : 'text-red-600'}`}>
                                  {data.volume_above_avg ? '↑' : '↓'}
                                </span>
                              )}
                              <div className="text-lg font-bold">{data.current_price?.toFixed(2)}</div>
                            </div>
                            <div className={`text-xs font-medium ${
                              data.price_change >= 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {data.price_change >= 0 ? '+' : ''}{data.price_change?.toFixed(2)} 
                              ({data.price_change >= 0 ? '+' : ''}{data.price_change_pct?.toFixed(2)}%)
                            </div>
                            <div className="text-xs text-gray-500 space-y-0.5 mt-0.5">
                              {data.sma_5 && <div>5: {data.sma_5.toFixed(2)}</div>}
                              {data.sma_50 && <div>50: {data.sma_50.toFixed(2)}</div>}
                              {data.sma_100 && <div>100: {data.sma_100.toFixed(2)}</div>}
                              {data.sma_200 && <div>200: {data.sma_200.toFixed(2)}</div>}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-2 pt-1">
                        <CandlestickChart
                          data={data.chart_data}
                          indicators={mag7Indicators}
                          height={
                            mag7GridLayout === '1x8' ? 400 :
                            mag7GridLayout === '2x4' ? 500 :
                            mag7GridLayout === '3x3' ? 300 :
                            280
                          }
                          darkMode={darkMode}
                        />
                      </CardContent>
                    </Card>
                  ) : (
                    <Card key={ticker}>
                      <CardContent className="p-6 text-center">
                        <div className="text-gray-500">
                          <div className="font-medium">{ticker}</div>
                          <div className="text-sm">No data available</div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="space-y-4">
                    <div className="text-gray-500">
                      <div className="text-lg font-medium">No MAG7 Data Available</div>
                      <div className="text-sm">Click refresh to load data</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news" className="space-y-6">
            {newsLoading ? (
              <Card>
                <CardContent className="p-12">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                    <div className="text-lg font-medium">Loading news for {ticker}...</div>
                  </div>
                </CardContent>
              </Card>
            ) : newsData.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[800px]">
                {/* Left Column - News List */}
                <Card className="overflow-hidden">
                  <CardHeader className="bg-emerald-50 dark:bg-emerald-950">
                    <CardTitle>Latest News - {ticker}</CardTitle>
                    <CardDescription>Click on any news item to view full article</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 overflow-y-auto h-[calc(800px-100px)]">
                    {newsData.map((news) => (
                      <div
                        key={news.id}
                        onClick={() => setSelectedNews(news)}
                        className={`p-4 border-b cursor-pointer transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-950 ${
                          selectedNews?.id === news.id 
                            ? 'bg-emerald-100 dark:bg-emerald-900 border-l-4 border-l-emerald-600' 
                            : ''
                        }`}
                      >
                        <h3 className="font-semibold text-base mb-2 line-clamp-2">
                          {news.title || 'Untitled'}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                          {news.summary || news.full_content?.substring(0, 150) + '...'}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-500">
                          {news.source && (
                            <span className="flex items-center gap-1">
                              <span className="font-medium">{news.source}</span>
                            </span>
                          )}
                          {news.date && (
                            <span>• {news.date}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Right Column - Full Article */}
                <Card className="overflow-hidden">
                  <CardHeader className="bg-emerald-50 dark:bg-emerald-950">
                    <CardTitle>Article Details</CardTitle>
                    {selectedNews?.url && (
                      <a 
                        href={selectedNews.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 hover:underline"
                      >
                        View Original Source →
                      </a>
                    )}
                  </CardHeader>
                  <CardContent className="p-6 overflow-y-auto h-[calc(800px-120px)]">
                    {selectedNews ? (
                      <div className="space-y-4">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {selectedNews.title || 'Untitled Article'}
                        </h2>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 pb-4 border-b">
                          {selectedNews.source && (
                            <div>
                              <span className="font-semibold">Source:</span> {selectedNews.source}
                            </div>
                          )}
                          {selectedNews.date && (
                            <div>
                              <span className="font-semibold">Date:</span> {selectedNews.date}
                            </div>
                          )}
                        </div>

                        {selectedNews.summary && (
                          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                            <h3 className="font-semibold mb-2">Summary</h3>
                            <p className="text-gray-700 dark:text-gray-300">{selectedNews.summary}</p>
                          </div>
                        )}

                        <div className="prose dark:prose-invert max-w-none">
                          <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                            {selectedNews.full_content}
                          </div>
                        </div>

                        {selectedNews.url && (
                          <div className="pt-4 border-t">
                            <a 
                              href={selectedNews.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                            >
                              Read Full Article
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        Select a news item from the left to view details
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="p-12">
                  <div className="text-center space-y-4">
                    <div className="text-lg font-medium text-gray-600 dark:text-gray-400">
                      No news available for {ticker}
                    </div>
                    <Button onClick={fetchNews} className="bg-emerald-600 hover:bg-emerald-700">
                      Retry Loading News
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

        </Tabs>
    </div>
  );
}

export default App;
